import React, { useEffect, useMemo, useState } from 'react';
import type { AtsModelInfo, AtsRecord } from '../../lib/api';
import ComparePanel from './ComparePanel';
import MatchReport from './MatchReport';
import Workspace from './Workspace';
import type { AtsJob } from './NewResume';
import { Btn, C, ModelChip, Stepper, fmtTimer } from './ui';

// One ATS resume, at whichever step it's on: the AI working (a waiting
// screen), ② the match report, the model comparison, or ③ the resume.

const BUSY: Record<'check' | 'make', { title: string; lines: string[]; step: 'check' | 'make' }> = {
  check: {
    title: 'Reading the resume and the job…', step: 'check',
    lines: ['Reading every line of the resume', "Finding the job's ATS keywords", 'Checking eligibility — education is never changed', 'Seeing how each project fits this job'],
  },
  make: {
    title: 'Writing the ATS resume…', step: 'make',
    lines: ["Rewording each line with the job's words", 'Checking every line against the facts', 'Putting the best skills and projects first', 'Writing the HR message and suggestions'],
  },
};

const BusyScreen: React.FC<{ rec: AtsRecord }> = ({ rec }) => {
  const b = BUSY[rec.busy as 'check' | 'make'];
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const seconds = useMemo(() => (rec.busySince ? Math.max(0, Math.round((now - Date.parse(rec.busySince)) / 1000)) : 0), [now, rec.busySince]);
  const model = rec.models[b.step];
  return (
    <div className="max-w-2xl mx-auto ats-fade">
      <div className="relative overflow-hidden bg-white rounded-3xl border border-gray-100 ats-card px-6 py-12 text-center">
        <div className="absolute inset-x-0 -top-24 h-48 ats-glow" style={{ background: 'radial-gradient(ellipse at center, rgba(240,180,41,.22), transparent 70%)' }} />
        <div className="relative mx-auto w-28 h-28">
          <div className="absolute inset-0 rounded-full ats-orb" style={{ background: `conic-gradient(from 0deg, ${C.gold}, ${C.orange}, ${C.purple2}, ${C.purple}, ${C.gold})` }} />
          <div className="absolute inset-[7px] rounded-full bg-white flex items-center justify-center text-4xl">✨</div>
        </div>
        <p className="relative text-xl font-black mt-6 ats-shimmer">{b.title}</p>
        <div className="relative mt-5 inline-flex flex-col gap-2 text-left">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-400">What the AI is doing</p>
          {b.lines.map((l, i) => (
            <p key={l} className="flex items-center gap-2.5 text-sm text-gray-700 font-semibold">
              <span className="w-2 h-2 rounded-full ats-glow" style={{ background: C.gold, animationDelay: `${i * 0.3}s` }} />
              {l}
            </p>
          ))}
        </div>
        <div className="relative flex items-center justify-center gap-3 mt-6">
          <ModelChip id={model} />
          <span className="text-xs font-bold text-gray-500 tabular-nums">{fmtTimer(seconds)}</span>
        </div>
        <p className="relative text-xs text-gray-400 mt-3">
          {model === 'claude-fable-5-1' ? 'Fable is thorough — it can take 2 to 4 minutes.' : 'Usually about a minute.'} You can leave this page — it keeps going.
        </p>
      </div>
    </div>
  );
};

const AtsRecordView: React.FC<{
  rec: AtsRecord; pw: string; jobs: AtsJob[]; models: AtsModelInfo[];
  onChanged: (r?: AtsRecord) => void; onOpen: (id: string) => void; onRetry: () => void;
}> = ({ rec, pw, jobs, models, onChanged, onOpen, onRetry }) => {
  const [mode, setMode] = useState<'auto' | 'match' | 'compare'>('auto');
  useEffect(() => { setMode('auto'); }, [rec._id]);

  if (rec.busy === 'check' || rec.busy === 'make') return <div className="space-y-5"><Stepper at={rec.busy === 'check' ? 2 : 3} /><BusyScreen rec={rec} /></div>;

  if (!rec.check) {
    return (
      <div className="max-w-xl mx-auto rounded-3xl p-8 border-2 text-center bg-white ats-fade" style={{ borderColor: '#fecaca' }}>
        <p className="text-4xl">😕</p>
        <p className="font-black text-lg text-red-800 mt-2">The check didn't work</p>
        <p className="text-sm text-red-700 mt-1">{rec.error || 'Something went wrong.'}</p>
        <Btn kind="gold" size="lg" className="mt-5" onClick={onRetry}>Try again</Btn>
      </div>
    );
  }

  const view = mode !== 'auto' ? mode : rec.step === 'done' ? 'resume' : rec.compare ? 'compare' : 'match';
  return (
    <div className="space-y-5">
      <Stepper at={view === 'resume' ? 3 : 2} />
      {rec.error && !rec.busy && (
        <div className="rounded-2xl px-4 py-3 text-sm text-red-800 bg-red-50 border border-red-100 ats-fade">⚠️ {rec.error}</div>
      )}
      {view === 'compare' && rec.compare ? (
        <ComparePanel rec={rec} pw={pw} onChanged={r => { setMode('auto'); onChanged(r); }}
          onBack={() => setMode(rec.step === 'done' ? 'auto' : 'match')} backLabel={rec.step === 'done' ? 'Back to the resume' : 'Back to the match report'} />
      ) : view === 'resume' && rec.resume ? (
        <Workspace rec={rec} pw={pw} jobs={jobs} onChanged={onChanged} onOpen={onOpen}
          onRechoose={() => setMode('match')} onCompare={() => setMode('compare')} />
      ) : (
        <>
          {rec.step === 'done' && (
            <div className="rounded-2xl px-4 py-3 text-xs flex items-center justify-between gap-3 bg-amber-50 border border-amber-200">
              <span className="text-amber-900">Making it again replaces the current resume, including your edits. The chat stays.</span>
              <button onClick={() => setMode('auto')} className="font-extrabold text-purple-700 whitespace-nowrap">← Back to the resume</button>
            </div>
          )}
          {rec.step !== 'done' && rec.compare && (
            <button onClick={() => setMode('compare')} className="text-xs font-extrabold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">⚖ See the model comparison</button>
          )}
          <MatchReport rec={rec} pw={pw} models={models} onStarted={() => { setMode('auto'); onChanged(); }} />
        </>
      )}
    </div>
  );
};

export default AtsRecordView;
